import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setDrawer } from "../../../redux-store/features/layoutSlice";
import {
  useAddSponsorUser,
  useGetAllProviderUsersByProviderId,
  useGetAllSponsorsForAdmin,
  useGetAllSponsorUserList,
  useGetSponsorUserDetails,
  useUpdateSponsorUser,
  // useCreateProviderUser, // COMMENTED OUT - Dual Role
} from "../../../custom-hooks/queries";
import ExistingUserSelector from "./ExistingUserSelector";
import NotificationModal from "../../../components/NotificationModal";
import { v4 as uuidv4 } from "uuid";
import FormSkeletonLoader from "../../../components/FormSkeletonLoader";
import MfaReadOnlyField from "../../../components/MfaReadOnlyField";
import {
  rightsCategories,
  rightsDescriptions,
  rightsList,
  roleDefinitions,
  vendorRightsList,
  vendorRoleDefinitions,
  RIGHTS_DISPLAY_NAMES,
  ROLE_DISPLAY_NAMES,
  PERMISSION_CATEGORY_DISPLAY_NAMES,
} from "../../../utils/constants";
import { isVqaOnlyOrg, resolveUserHyperComplyOrg } from "../../../utils/vqaOnlyOrg";
import { supportedEmailCharacters } from "../../../utils/emailSupportedCharacters";
import {
  DEFAULT_NEW_SPONSOR_SUBROLES,
  applySponsorPermissionsForRoles,
  isUserRoleSelectionSaveDisabled,
} from "../../../utils/userRolePermissionDefaults";

/** Maps ExistingUserSelector sponsor-role dropdown ids to form role names */
const EXISTING_PROVIDER_ROLE_ID_TO_SPONSOR_ROLE = {
  admin: "Sponsor Admin",
  "rfi-manager": "RFI Manager",
  "audit-manager": "VQA Manager",
};

/** Reusable text input component */
const FormInput = ({
  label,
  name,
  register,
  errors,
  placeholder,
  numeric,
  type = "text",
  options,
  disabled = false,
  className = "",
}) => {
  // Phone number input masking for format X-XXX-XXX-XXXX
  const handlePhoneInput = (e) => {
    let value = e.target.value.replace(/[^\d]/g, "");
    if (value.length > 11) value = value.slice(0, 11);
    let formatted = value;
    if (value.length > 0) {
      formatted = value[0];
      if (value.length > 1) {
        formatted += "-" + value.slice(1, 4);
      }
      if (value.length > 4) {
        formatted += "-" + value.slice(4, 7);
      }
      if (value.length > 7) {
        formatted += "-" + value.slice(7, 11);
      }
    }
    e.target.value = formatted;
  };

  if (type === "select") {
    return (
      <div className={`flex flex-col ${className}`}>
        <label className="block text-sm font-medium text-gray-900">
          {label}
        </label>
        <select
          {...register(name)}
          disabled={disabled}
          className="mt-1 ring-0 border-gray-300 rounded-md text-sm"
        >
          <option value="">-- Select --</option>
          {(options || []).map((opt) => (
            <option
              key={typeof opt === "object" ? opt.value || opt.sponsor_id : opt}
              value={
                typeof opt === "object" ? opt.value || opt.sponsor_id : opt
              }
            >
              {typeof opt === "object" ? opt.label || opt.sponsor_name : opt}
            </option>
          ))}
        </select>
        {errors[name] && (
          <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <label className="block text-sm font-medium text-gray-900">{label}</label>
      <input
        type={type}
        {...register(name)}
        placeholder={
          name === "phone_number" || name === "office_phone_number"
            ? "1-234-567-8900"
            : placeholder
        }
        disabled={disabled}
        onInput={
          name === "phone_number" || name === "office_phone_number"
            ? handlePhoneInput
            : undefined
        }
        {...(name === "phone_number" || name === "office_phone_number"
          ? { maxLength: 14 }
          : {})}
        onKeyPress={
          numeric
            ? (e) => {
                if (!/^[0-9]*$/.test(e.key)) e.preventDefault();
              }
            : undefined
        }
        className={`mt-1 w-full border-gray-300 rounded-md text-sm ${
          disabled ? "bg-gray-100" : ""
        }`}
      />
      {errors[name] && (
        <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
      )}
    </div>
  );
};

/** Reusable radio group component for contact preference */
const RadioGroup = ({ label, name, register, errors }) => (
  <div className="flex flex-col">
    <label className="block text-sm font-medium text-gray-900">{label}</label>
    <div className="flex items-center gap-6 mt-1">
      <label className="flex items-center">
        <input
          type="radio"
          {...register(name)}
          value="Mobile_Phone"
          className="mr-2"
        />
        Mobile Phone
      </label>
      <label className="flex items-center">
        <input
          type="radio"
          {...register(name)}
          value="Office_Phone"
          className="mr-2"
        />
        Office Phone
      </label>
    </div>
    {errors[name] && (
      <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
    )}
  </div>
);

const AddSponsorUsers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const sponsorIdFromUrl = searchParams.get("sponsorId");
  const appliedUrlSponsorParamRef = useRef(null);

  // API calls
  const { data: sponsors } = useGetAllSponsorsForAdmin({
    page_size: 1000,
    current_page: 1,
  });

  // Add state to track if first load has completed
  const [firstLoadComplete, setFirstLoadComplete] = useState(false);

  // Use a timestamp to force cache invalidation
  const [refreshKey, setRefreshKey] = useState(Date.now());

  // Improve the query with better options
  const {
    data: userDetails,
    isLoading: isLoadingUserDetails,
    refetch,
  } = useGetSponsorUserDetails(userId, {
    enabled: !!userId,
    refetchOnWindowFocus: false,
    staleTime: 0, // Don't use stale data at all
    cacheTime: 0, // Don't cache this data
    retry: 3, // Try up to 3 times if it fails
    // Add this key to force a re-fetch whenever it changes
    queryKey: ["sponsorUserDetails", userId, refreshKey],
    onSuccess: (data) => {
      // Force a complete reset of the form whenever we get fresh data
      if (data) {
        setTimeout(() => {
          setUserDataLoaded(false); // Reset this flag to trigger the useEffect
          setFirstLoadComplete(true);
        }, 100);
      }
    },
  });

  // Add this effect to force refetch on mount
  useEffect(() => {
    if (userId) {
      // Force invalidate cache and refetch when component mounts
      setRefreshKey(Date.now());
      refetch();

      // Create a timer to check if data loaded properly after 2 seconds
      const timer = setTimeout(() => {
        refetch();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [userId]);

  const { mutate: addSponsor } = useAddSponsorUser();
  const { mutate: updateSponsor } = useUpdateSponsorUser();
  // const { mutate: addProviderUser } = useCreateProviderUser(); // COMMENTED OUT - Dual Role

  const [alertOpen, setAlertOpen] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [isSuccess, setIsSuccess] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [userDataLoaded, setUserDataLoaded] = React.useState(false);

  // Dual role state - COMMENTED OUT
  // const [enableDualRole, setEnableDualRole] = useState(false);
  // const [selectedVendorRoles, setSelectedVendorRoles] = useState([]);

  // Update schema to include is_active field when editing
  const schema = yup.object().shape({
    sponsor_id: yup.string().required("Sponsor organization is required"),
    email: yup
      .string()
      .email("Enter a valid email")
      .test(supportedEmailCharacters)
      .required("Email is required"),
    first_name: yup.string().required("First name is required"),
    last_name: yup.string().required("Second name is required"),
    job_title: yup
      .string()
      .nullable()
      .transform((value) => (value === "" || value === undefined ? null : value)),
    office_phone_number: yup
      .string()
      .matches(
        /^\d{1}-\d{3}-\d{3}-\d{4}$/,
        "Please enter a valid office phone number (format: X-XXX-XXX-XXXX)"
      )
      .nullable()
      .transform((value) => (value === "" ? null : value)),
    phone_number: yup
      .string()
      .matches(
        /^\d{1}-\d{3}-\d{3}-\d{4}$/,
        "Please enter a valid mobile phone number (format: X-XXX-XXX-XXXX)"
      )
      .nullable()
      .transform((value) => (value === "" ? null : value)),

    contact_preference: yup.string().required("Contact preference is required"),
    role: yup.array().min(1, "At least one role must be selected"),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
    defaultValues: {
      contact_preference: "Mobile_Phone",
      role: DEFAULT_NEW_SPONSOR_SUBROLES,
      is_active: true,
      job_title: "",
    },
  });

  const selectedRoles = watch("role") || [];
  const selectedSponsorId = watch("sponsor_id");

  const selectedSponsorForOrg = sponsors?.items?.find(
    (s) =>
      (s.sponsor_id || "").toLowerCase() ===
      (selectedSponsorId || "").toLowerCase()
  );
  const providerIdForDualPortal =
    selectedSponsorForOrg?.provider_id ||
    selectedSponsorForOrg?.sponsor_id ||
    null;

  const { data: providerUsersData } = useGetAllProviderUsersByProviderId(
    providerIdForDualPortal,
    1000,
    1,
    ""
  );
  // Users already on the sponsor side of this org: a carry-over candidate
  // whose email already exists here must not be offered again (14464). The
  // isdual_role flag is not reliable for this (it is cleared by ordinary
  // user updates), so exclusion is by email.
  const { data: currentSponsorUsersData } = useGetAllSponsorUserList({
    currentPage: 1,
    id: selectedSponsorId || null,
    search_text: "",
    page_size: 1000,
  });
  const existingSponsorEmails = new Set(
    (currentSponsorUsersData?.items ?? [])
      .map((u) => u.email?.toLowerCase())
      .filter(Boolean)
  );
  const availableProviderUsers =
    providerUsersData?.items?.filter(
      (u) => u.email && !existingSponsorEmails.has(u.email.toLowerCase())
    ) ?? [];
  const orgIsDualRole =
    (selectedSponsorForOrg?.isdual_role || providerUsersData?.items?.some((u) => u.isdual_role)) ?? false;

  // Create a state to track if roles have been manually changed after initial load
  const [rolesManuallyChanged, setRolesManuallyChanged] = useState(false);

  // Load user data if in edit mode and data is available
  useEffect(() => {
    // Only load user data if we're in edit mode (userId exists)
    // AND we have user details AND the data hasn't been loaded yet
    if (userId && userDetails && (!userDataLoaded || !firstLoadComplete)) {
      // Step 0: Clear the entire form first
      reset({}, { keepDefaultValues: false });

      // Step 1: Wait a tick for React to process the reset
      setTimeout(() => {
        // Step 2: Set the basic user information
        reset({
          sponsor_id: userDetails?.sponsor_id?.toLowerCase(),
          email: userDetails.email,
          first_name: userDetails.first_name,
          last_name: userDetails.last_name,
          job_title: userDetails.job_title ?? userDetails.title ?? "",
          phone_number: userDetails.phone_number,
          office_phone_number: userDetails.office_phone_number || "",
          contact_preference: userDetails.contact_preference || "Mobile_Phone",
          is_active: userDetails.is_active !== false, // Default to true if not specified
          country: userDetails.country || "",
          role: [], // We'll set this separately
        });

        // Step 3: Reset all permissions to false first
        rightsList.forEach((right) => {
          setValue(right, false);
        });

        // Step 4: Set roles with another small delay
        setTimeout(() => {
          if (
            userDetails.sponsor_subrole_dto &&
            userDetails.sponsor_subrole_dto.length > 0
          ) {
            const roles = userDetails.sponsor_subrole_dto.map(
              (role) => role.sub_role
            );
            setValue("role", roles);
          }

          // Step 5: Final step - explicitly set permissions from API
          setTimeout(() => {
            if (
              userDetails.sponsor_rights_dto &&
              userDetails.sponsor_rights_dto.length > 0
            ) {
              userDetails.sponsor_rights_dto.forEach((right) => {
                if (right && right.rights) {
                  setValue(right.rights, true, { shouldValidate: true });
                }
              });
            }
            // Chat Diligent access is stored separately on the user, not in sponsor_rights_dto
            setValue(
              "Access to Chat Diligent",
              userDetails.chat_diligent_access === true,
              { shouldValidate: true }
            );

            // Ensure My Vendors is selected by default for RFI Manager and Sponsor Admin
            const userRoles =
              userDetails.sponsor_subrole_dto?.map((role) => role.sub_role) ||
              [];
            if (
              userRoles.includes("RFI Manager") ||
              userRoles.includes("Sponsor Admin")
            ) {
              setValue("My Vendors", true, { shouldValidate: true });
            }

            // Mark that we've loaded the initial user data
            setUserDataLoaded(true);
          }, 100);
        }, 100);
      }, 100);
    } else if (!userId) {
      reset({
        contact_preference: "Mobile_Phone",
        role: DEFAULT_NEW_SPONSOR_SUBROLES,
        is_active: true,
        job_title: "",
      });

      applySponsorPermissionsForRoles(setValue, DEFAULT_NEW_SPONSOR_SUBROLES);
    }
  }, [userDetails, reset, setValue, userDataLoaded, firstLoadComplete, userId]);

  // Pre-select sponsor when ?sponsorId= is in the URL (create mode only).
  // Ref avoids re-applying on sponsor list refetch so manual changes are not overwritten.
  useEffect(() => {
    if (userId) {
      appliedUrlSponsorParamRef.current = null;
      return;
    }
    if (!sponsorIdFromUrl) {
      appliedUrlSponsorParamRef.current = null;
      return;
    }
    if (!sponsors?.items?.length) return;

    const match = sponsors.items.find((s) => {
      const sid = (s.sponsor_id || s.value || "").toLowerCase();
      return sid === sponsorIdFromUrl.toLowerCase();
    });
    if (!match) return;

    const optionValue = (match.value || match.sponsor_id || "").toLowerCase();
    if (appliedUrlSponsorParamRef.current === sponsorIdFromUrl) return;

    setValue("sponsor_id", optionValue, { shouldValidate: true });
    appliedUrlSponsorParamRef.current = sponsorIdFromUrl;
  }, [userId, sponsorIdFromUrl, sponsors, setValue]);

  const syncPermissionsAfterRoleChange = (newRoles) => {
    setTimeout(() => {
      setRolesManuallyChanged(true);
      applySponsorPermissionsForRoles(setValue, newRoles, {
        preserveTrialDashboard: true,
        preserveChatDiligent: true,
        getValues,
        setValueOptions: { shouldDirty: true },
      });
    }, 100);
  };

  // Handle role selection with delayed permission updates
  const handleRoleChange = (e, role) => {
    const isChecked = e.target.checked;

    const currentRoles = getValues("role") || [];
    let newRoles = [];

    if (isChecked) {
      if (role === "Sponsor Admin") {
        newRoles = ["Sponsor Admin"];
      } else if (currentRoles.includes("Sponsor Admin")) {
        newRoles = [role];
      } else {
        newRoles = [...currentRoles, role];
      }
    } else {
      newRoles = currentRoles.filter((r) => r !== role);
    }

    setValue("role", newRoles);
    syncPermissionsAfterRoleChange(newRoles);
  };

  // Function to check if a role should be disabled
  const isRoleDisabled = (role) => {
    const currentRoles = getValues("role") || [];
    if (role === "Sponsor Admin") {
      // Disable Sponsor Admin if any other role is selected
      return currentRoles.some((r) => r !== "Sponsor Admin" && r !== role);
    } else {
      // Disable other roles if Sponsor Admin is selected
      return currentRoles.includes("Sponsor Admin");
    }
  };

  const onExistingProviderUserSelect = (user) => {
    if (user) {
      setValue("first_name", user.first_name || "");
      setValue("last_name", user.last_name || "");
      setValue("job_title", user.job_title || user.title || "");
      setValue("email", user.email || "");
      setValue("phone_number", user.phone_number || "");
      setValue("office_phone_number", user.office_phone_number || "");
      setValue("contact_preference", user.contact_preference || "Mobile_Phone");
      const defaultRole = EXISTING_PROVIDER_ROLE_ID_TO_SPONSOR_ROLE.admin;
      setValue("role", [defaultRole]);
      syncPermissionsAfterRoleChange([defaultRole]);
    } else {
      setValue("first_name", "");
      setValue("last_name", "");
      setValue("job_title", "");
      setValue("email", "");
      setValue("phone_number", "");
      setValue("office_phone_number", "");
      setValue("contact_preference", "Mobile_Phone");
      setValue("role", DEFAULT_NEW_SPONSOR_SUBROLES);
      syncPermissionsAfterRoleChange(DEFAULT_NEW_SPONSOR_SUBROLES);
    }
  };

  const onExistingProviderUserRoleChange = (_userId, roleId) => {
    const sponsorRole = EXISTING_PROVIDER_ROLE_ID_TO_SPONSOR_ROLE[roleId];
    if (!sponsorRole) return;
    setValue("role", [sponsorRole]);
    syncPermissionsAfterRoleChange([sponsorRole]);
  };

  // Get vendor permissions based on selected vendor roles - COMMENTED OUT (Dual Role)
  // const getVendorPermissionsForRoles = (vendorRoles) => {
  //   const permissions = new Set();

  //   vendorRoles.forEach((role) => {
  //     if (role === "Vendor Admin") {
  //       vendorRightsList.forEach((right) => {
  //         if (
  //           right !== "Data Rooms" &&
  //           right !== "Trust Pages" &&
  //           right !== "All Questionnaires"
  //         ) {
  //           permissions.add(right);
  //         }
  //       });
  //     } else if (
  //       role === "RFI Manager - Approver" ||
  //       role === "RFI Manager - User"
  //     ) {
  //       const rfiPermissions = [
  //         "View Requested RFIs",
  //         "Request RFIs",
  //         "Compare RFI",
  //         "View Released RFIs",
  //         "My Vendors",
  //       ];
  //       rfiPermissions.forEach((p) => {
  //         if (vendorRightsList.includes(p)) permissions.add(p);
  //       });
  //     } else if (role === "VQA Manager") {
  //       const vqaPermissions = [
  //         "View and Request Library VQAs",
  //         "View Requested VQAs",
  //       ];
  //       vqaPermissions.forEach((p) => {
  //         if (vendorRightsList.includes(p)) permissions.add(p);
  //       });
  //     }
  //   });

  //   return Array.from(permissions);
  // };

  // Handle vendor role selection for dual role - COMMENTED OUT (Dual Role)
  // const handleVendorRoleChange = (e, role) => {
  //   const isChecked = e.target.checked;
  //   let newRoles = [];

  //   if (isChecked) {
  //     if (role === "Vendor Admin") {
  //       newRoles = ["Vendor Admin"];
  //     } else if (selectedVendorRoles.includes("Vendor Admin")) {
  //       newRoles = [role];
  //     } else {
  //       newRoles = [...selectedVendorRoles, role];
  //     }
  //   } else {
  //     newRoles = selectedVendorRoles.filter((r) => r !== role);
  //   }

  //   setSelectedVendorRoles(newRoles);
  // };

  // Check if vendor role should be disabled - COMMENTED OUT (Dual Role)
  // const isVendorRoleDisabled = (role) => {
  //   if (role === "Vendor Admin") {
  //     return selectedVendorRoles.some((r) => r !== "Vendor Admin");
  //   } else {
  //     return selectedVendorRoles.includes("Vendor Admin");
  //   }
  // };

  // Modified onSubmit function to handle both create and update
  const onSubmit = (data) => {
    setIsLoading(true);

    // Ensure we have selected roles
    if (!selectedRoles || selectedRoles.length === 0) {
      setIsSuccess(false);
      setMessage("Please select at least one role");
      setAlertOpen(true);
      setIsLoading(false);
      return;
    }

    // Get the sponsor name from the selected sponsor ID
    const selectedSponsor = sponsors?.items?.find(
      (s) => s.sponsor_id === data.sponsor_id
    );
    const sponsorName = selectedSponsor?.sponsor_name || "";
    const hyperComplyOrg = resolveUserHyperComplyOrg(
      isVqaOnlyOrg(selectedSponsor),
      selectedSponsor?.hypercomply_org_id
    );
    if (!selectedSponsor || !hyperComplyOrg.ok) {
      setIsSuccess(false);
      setMessage(
        "Could not resolve organization for the selected sponsor. Please reselect it from the list."
      );
      setAlertOpen(true);
      setIsLoading(false);
      return;
    }

    // Get only the rights that are checked (ChatDiligentAccess comes from "Access to Chat Diligent" in sponsor_rights_dto)
    const rights = rightsList.filter((right) => data[right] === true);

    // Prepare roles as array
    const rolesArray = Array.isArray(selectedRoles)
      ? selectedRoles
      : [selectedRoles];

    // Common data for both create and update
    const sponsorDataCreate = {
      email: data.email,
      phone_number: data.phone_number,
      office_phone_number: data.office_phone_number,
      contact_preference: data.contact_preference,
      first_name: data.first_name,
      last_name: data.last_name,
      job_title: (data.job_title ?? "").trim() || null,
      sponsor_id: data.sponsor_id,
      sponsor_name: sponsorName,
      is_active: userId ? data.is_active : true,
      hypercomply_org_id: hyperComplyOrg.hypercomplyOrgId,

      // Generate sponsor_subrole_dto with unique IDs
      sponsor_subrole_dto: rolesArray.map((role) => ({
        sponsor_subrole_id: uuidv4(),
        sub_role: role,
      })),

      // Generate sponsor_rights_dto with unique IDs
      sponsor_rights_dto: rights.map((right) => ({
        sponsor_rights_id: uuidv4(),
        rights: right,
      })),
    };
    const sponsorDataUpdate = {
      email: data.email,
      phone_number: data.phone_number,
      office_phone_number: data.office_phone_number,
      contact_preference: data.contact_preference,
      first_name: data.first_name,
      last_name: data.last_name,
      job_title: (data.job_title ?? "").trim() || null,
      sponsor_id: data.sponsor_id,
      sponsor_name: sponsorName,
      is_active: data.is_active,

      sponsor_subrole_dto: rolesArray.map((role) => ({
        sponsor_subrole_id: uuidv4(),
        sub_role: role,
      })),

      // Generate sponsor_rights_dto with unique IDs
      sponsor_rights_dto: rights.map((right) => ({
        sponsor_rights_id: uuidv4(),
        rights: right,
      })),
    };

    if (userId) {
      // Update existing user: use sponsorDataUpdate (includes is_active; chat via sponsor_rights_dto)
      updateSponsor(
        { id: userId, userData: sponsorDataUpdate },
        {
          onSuccess: () => {
            // Force refetch after update succeeds
            setRefreshKey(Date.now());
            refetch();

            // Show success message with a delay to allow refetch to complete
            setTimeout(() => {
              setIsLoading(false);
              setIsSuccess(true);
              setMessage("User updated successfully!");
              setAlertOpen(true);

              // Add a longer delay before navigation to ensure data is refreshed
              setTimeout(
                () => navigate(`/Backend/Users/Sponsors?tab=users`),
                2000
              );
            }, 300);
          },
          onError: (error) => {
            setIsLoading(false);
            setIsSuccess(false);

            // Safely extract the error message
            let errorMessage = "Failed to update user";

            if (error.response?.data) {
              // Check if data is an object with message property
              if (
                typeof error.response.data === "object" &&
                error.response.data.message
              ) {
                errorMessage = error.response.data.message;
              }
              // Check if data is an object with code and message properties
              else if (
                typeof error.response.data === "object" &&
                error.response.data.code
              ) {
                errorMessage = `Error ${error.response.data.code}: ${
                  error.response.data.message || "Unknown error"
                }`;
              }
              // If it's a string, use it directly
              else if (typeof error.response.data === "string") {
                errorMessage = error.response.data;
              }
            }

            setMessage(errorMessage);
            setAlertOpen(true);
          },
        }
      );
    } else {
      // Create new user
      // COMMENTED OUT - Dual Role Logic
      // if (enableDualRole) {
      //   // Validate vendor roles are selected
      //   if (selectedVendorRoles.length === 0) {
      //     setIsSuccess(false);
      //     setMessage("Please select at least one vendor role for dual role");
      //     setAlertOpen(true);
      //     setIsLoading(false);
      //     return;
      //   }

      //   // Create user in both sponsor and vendor portals
      //   addSponsor(sponsorDataCreate, {
      //     onSuccess: () => {
      //       const vendorPermissions =
      //         getVendorPermissionsForRoles(selectedVendorRoles);

      //       const providerUserData = {
      //         email: data.email,
      //         provider_user_id: uuidv4(),
      //         provider_id:
      //           selectedSponsor?.provider_id || selectedSponsor?.sponsor_id,
      //         phone_number: data.phone_number,
      //         office_phone_number: data.office_phone_number,
      //         contact_preference: data.contact_preference,
      //         first_name: data.first_name,
      //         last_name: data.last_name,
      //         provider_name: selectedSponsor?.vendor_name || sponsorName,
      //         organization_id: hyperComplyOrgId,
      //         provider_user_subrole: selectedVendorRoles.map((role) => ({
      //           Subrole: role,
      //         })),
      //         provider_user_permissions: vendorPermissions.map((right) => ({
      //           Permission: right,
      //         })),
      //       };

      //       addProviderUser(
      //         { providerData: providerUserData },
      //         {
      //           onSuccess: () => {
      //             setIsLoading(false);
      //             setIsSuccess(true);
      //             setMessage(
      //               "User created in both Sponsor and Vendor portals!"
      //             );
      //             setAlertOpen(true);
      //             setTimeout(
      //               () => navigate(`/Backend/Users/Sponsors?tab=users`),
      //               2000
      //             );
      //           },
      //           onError: (error) => {
      //             setIsLoading(false);
      //             setIsSuccess(false);
      //             let errorMessage =
      //               "Sponsor user created, but failed to create vendor user";
      //             if (error.response?.data?.message) {
      //               errorMessage = error.response.data.message;
      //             }
      //             setMessage(errorMessage);
      //             setAlertOpen(true);
      //           },
      //         }
      //       );
      //     },
      //     onError: (error) => {
      //       setIsLoading(false);
      //       setIsSuccess(false);
      //       let errorMessage = "Failed to add user";
      //       if (error.response?.data) {
      //         if (
      //           typeof error.response.data === "object" &&
      //           error.response.data.message
      //         ) {
      //           errorMessage = error.response.data.message;
      //         } else if (typeof error.response.data === "string") {
      //           errorMessage = error.response.data;
      //         }
      //       }
      //       setMessage(errorMessage);
      //       setAlertOpen(true);
      //     },
      //   });
      // } else {
        // Single role creation (existing behavior)
        addSponsor(sponsorDataCreate, {
          onSuccess: () => {
            setIsLoading(false);
            setIsSuccess(true);
            setMessage("User added successfully!");
            setAlertOpen(true);
            setTimeout(
              () => navigate(`/Backend/Users/Sponsors?tab=users`),
              2000
            );
          },
          onError: (error) => {
            setIsLoading(false);
            setIsSuccess(false);

            let errorMessage = "Failed to add user";

            if (error.response?.data) {
              if (
                typeof error.response.data === "object" &&
                error.response.data.message
              ) {
                errorMessage = error.response.data.message;
              } else if (
                typeof error.response.data === "object" &&
                error.response.data.code
              ) {
                errorMessage = `Error ${error.response.data.code}: ${
                  error.response.data.message || "Unknown error"
                }`;
              } else if (typeof error.response.data === "string") {
                errorMessage = error.response.data;
              }
            }

            setMessage(errorMessage);
            setAlertOpen(true);
          },
        });
      // } // COMMENTED OUT - End of dual role else block
    }
  };

  // Show loading state when fetching user details
  if (userId && isLoadingUserDetails) {
    return (
      <div className="w-full flex justify-center px-4">
        <div className="w-full max-w-2xl bg-white shadow-sm rounded-lg p-8">
          <p className="text-center mt-4 text-gray-600">
            <FormSkeletonLoader />
          </p>
        </div>
      </div>
    );
  }

  // Checkboxes for permissions with central management
  const PermissionCheckbox = ({ rightItem, category }) => {
    // Get the current value from the form - explicitly check for boolean true
    const currentValue = watch(rightItem) === true;

    // Determine if this right belongs to any of the selected roles
    const isRoleSpecificRight = selectedRoles.some((role) => {
      if (role === "Sponsor Admin") return true; // Admin can modify all rights

      // For other roles, check if this right belongs to their category
      if (role === "RFI Manager" && category === "RFI Permissions") return true;
      if (role === "VQA Manager" && category === "Assessment Permissions")
        return true;

      return false;
    });

    // General permissions are always editable if any role is selected
    const isGeneralPermission =
      category === "General Permissions" && selectedRoles.length > 0;

    // Admin permissions are only editable by Sponsor Admin
    const isAdminPermission =
      category === "Admin Permissions" &&
      selectedRoles.includes("Sponsor Admin");

    // Determine if this checkbox should be editable
    const isEditable =
      isRoleSpecificRight || isGeneralPermission || isAdminPermission;

    // For debugging - add data attributes
    const dataAttributes = {
      "data-checked": currentValue.toString(),
      "data-editable": isEditable.toString(),
    };

    return (
      <div className="flex items-start">
        <div className="flex items-center h-5 pt-1">
          <input
            type="checkbox"
            id={`right-${rightItem}`}
            checked={currentValue}
            disabled={!isEditable}
            onChange={(e) => {
              setValue(rightItem, e.target.checked, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            className={`h-4 w-4 rounded border-gray-300 ${
              isEditable
                ? "text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                : "text-gray-300 cursor-not-allowed"
            }`}
            {...dataAttributes}
          />
        </div>
        <div className={`ml-3 text-sm ${!isEditable ? "opacity-50" : ""}`}>
          <label
            htmlFor={`right-${rightItem}`}
            className={`font-medium ${
              isEditable ? "text-gray-700" : "text-gray-400"
            }`}
          >
            {RIGHTS_DISPLAY_NAMES[rightItem] || rightItem}
          </label>
          <p className="text-xs text-gray-500 mt-1">
            {rightsDescriptions[rightItem] || "No description available"}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full flex justify-center px-4">
      <NotificationModal
        open={alertOpen}
        setOpen={setAlertOpen}
        desc={message}
        title={isSuccess ? "Success" : "Error"}
        error={!isSuccess}
      />

      {/* Form container with half-width and centered */}
      <div className="w-full max-w-2xl bg-white shadow-sm rounded-lg">
        <form onSubmit={handleSubmit(onSubmit)} className="w-full p-6">
          {/* Header */}
          <div className="pb-5 border-b border-gray-200">
            <h1 className="font-semibold text-xl">
              {userId ? "Edit Sponsor User" : "Add New Sponsor User"}
            </h1>
            <p className="text-sm text-gray-600">
              {userId
                ? "Update the information for this sponsor user account."
                : "Fill in the information below to create a new sponsor user account."}
            </p>
          </div>

          {/* User Information */}
          <div className="mt-6 space-y-6">
            {/* Email - Readonly when editing */}
            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Email"
                name="email"
                register={register}
                errors={errors}
                type="email"
                placeholder="user@example.com"
                disabled={!!userId} // Email is readonly in edit mode
              />
            </div>

            {/* Sponsor Organization - Readonly when editing */}
            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Sponsor Organization"
                name="sponsor_id"
                register={register}
                errors={errors}
                type="select"
                options={sponsors?.items || []}
                // Readonly in edit mode, and in create mode when the org came
                // fixed from a Manage Org page (14464)
                disabled={!!userId || !!sponsorIdFromUrl}
              />
            </div>

            {!userId &&
              selectedSponsorForOrg &&
              orgIsDualRole &&
              providerIdForDualPortal &&
              availableProviderUsers.length > 0 && (
                <ExistingUserSelector
                  key={String(providerIdForDualPortal)}
                  type="vendor"
                  users={availableProviderUsers}
                  title="Select Existing Provider User"
                  description="This organization has dual portal access. Select a vendor-side user to create their sponsor-side account."
                  onUserSelect={onExistingProviderUserSelect}
                  onRoleChange={onExistingProviderUserRoleChange}
                />
              )}

            {/* Dual Role Toggle - Only show in create mode */}
            {/* {!userId && (
              <div className="border-b border-gray-200 pb-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="dual-role-toggle"
                    checked={enableDualRole}
                    onChange={(e) => {
                      setEnableDualRole(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedVendorRoles([]);
                      }
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="dual-role-toggle"
                    className="ml-3 text-sm font-medium text-gray-900"
                  >
                    Enable Dual Role
                  </label>
                </div>
                <p className="mt-1 text-sm text-gray-500 ml-7">
                  User will be created in both Sponsor and Vendor portals with
                  their respective permissions
                </p>
              </div>
            )} */}

            {/* Vendor Roles Selection - Only show when dual role is enabled */}
            {/* {!userId && enableDualRole && (
              <div className="border-b border-gray-200 pb-6">
                <div className="pb-3">
                  <h2 className="font-semibold text-lg text-gray-900">
                    Vendor Portal Roles
                  </h2>
                  <p className="text-sm text-gray-600">
                    Select the roles for the Vendor portal
                  </p>
                </div>
                <div className="space-y-4">
                  {Object.keys(vendorRoleDefinitions).map((role) => (
                    <div
                      key={`vendor-${role}`}
                      className="rounded-md px-2 py-1"
                    >
                      <div
                        className={`flex items-center ${
                          isVendorRoleDisabled(role) ? "opacity-50" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          id={`vendor-role-${role}`}
                          value={role}
                          checked={selectedVendorRoles.includes(role)}
                          onChange={(e) => handleVendorRoleChange(e, role)}
                          disabled={isVendorRoleDisabled(role)}
                          className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                            isVendorRoleDisabled(role)
                              ? "cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                        />
                        <label
                          htmlFor={`vendor-role-${role}`}
                          className="ml-3 flex-1"
                        >
                          <span className="block text-sm font-medium text-gray-900">
                            {role}
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-6">
              <FormInput
                label="First Name"
                name="first_name"
                register={register}
                errors={errors}
                placeholder="Enter first name"
                className="col-span-1"
              />
              <FormInput
                label="Last Name"
                name="last_name"
                register={register}
                errors={errors}
                placeholder="Enter last name"
                className="col-span-1"
              />
            </div>

            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Title"
                name="job_title"
                register={register}
                errors={errors}
                placeholder="e.g. VP of Clinical Operations"
              />
            </div>

            {/* Phone Numbers */}
            <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-6">
              <FormInput
                label="Mobile Phone Number"
                name="phone_number"
                register={register}
                errors={errors}
                placeholder="1-234-567-890"
                className="col-span-1"
              />
              <FormInput
                label="Office Phone Number"
                name="office_phone_number"
                register={register}
                errors={errors}
                placeholder="1-234-567-890"
                className="col-span-1"
              />
            </div>

            {/* Contact Preference */}
            <div className="border-b border-gray-200 pb-6">
              <RadioGroup
                label="Contact Preference"
                name="contact_preference"
                register={register}
                errors={errors}
              />
            </div>

            {/* Security: read-only MFA state, edit mode only (#14476) */}
            {userId && (
              <MfaReadOnlyField
                enabled={userDetails?.two_factor_enabled}
                idSuffix="sponsor-user"
              />
            )}

            {/* Roles Section */}
            <div className="pb-5 border-b border-gray-200">
              <h2 className="font-semibold text-lg">User Roles</h2>
            </div>

            <div className="border-b border-gray-200 pb-6 space-y-4">
              {Object.keys(roleDefinitions).map((role) => (
                <div key={role} className="rounded-md px-2 py-1">
                  <div
                    className={`flex items-center ${
                      isRoleDisabled(role) ? "opacity-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      id={`role-${role}`}
                      value={role}
                      checked={selectedRoles.includes(role)}
                      onChange={(e) => handleRoleChange(e, role)}
                      disabled={isRoleDisabled(role)}
                      className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                        isRoleDisabled(role)
                          ? "cursor-normal"
                          : "cursor-pointer"
                      }`}
                    />
                    <label htmlFor={`role-${role}`} className="ml-3 flex-1">
                      <span className="block text-sm font-medium text-gray-900">
                        {ROLE_DISPLAY_NAMES[role] || role}
                      </span>
                      <span className="block text-sm text-gray-500 mt-1">
                        {roleDefinitions[role].description || ""}
                      </span>
                    </label>
                  </div>
                </div>
              ))}
              {errors.role && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.role.message}
                </p>
              )}
              <input type="hidden" {...register("role")} />
            </div>

            {/* Permissions Section (includes "Access to Chat Diligent" under General Permissions) */}
            <div className="pb-5 border-b border-gray-200">
              <h2 className="font-semibold text-lg">User Permissions</h2>
              <p className="text-sm text-gray-600">
                Permissions will be automatically assigned based on selected
                roles.
              </p>
            </div>

            <div className="pb-6 space-y-6">
              {Object.entries(rightsCategories).map(
                ([category, categoryData]) => (
                  <div key={category} className=" rounded-md">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h3 className="text-sm font-medium text-gray-900">
                        {PERMISSION_CATEGORY_DISPLAY_NAMES[category] || category}
                      </h3>
                    </div>
                    <div className="p-4 space-y-4">
                      {categoryData.rights.map((rightItem) => (
                        <PermissionCheckbox
                          key={rightItem}
                          rightItem={rightItem}
                          category={category}
                        />
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Hidden inputs for all rights */}
            {rightsList.map((right) => (
              <input
                type="hidden"
                key={`hidden-${right}`}
                {...register(right)}
              />
            ))}

            {/* Form Buttons */}
            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate("/Backend/Users/Sponsors?tab=users")}
                className="rounded-md px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-100 focus:outline-none"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUserRoleSelectionSaveDisabled(isLoading, selectedRoles)}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none ${
                  isUserRoleSelectionSaveDisabled(isLoading, selectedRoles)
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-500"
                }`}
              >
                {isLoading ? "Saving..." : userId ? "Save Changes" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSponsorUsers;
